// src/components/NavigationMenu.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  List, ListItem, ListItemIcon, ListItemText, ListItemButton,
  Collapse, Typography, Box, Chip, Button, useMediaQuery
} from '@mui/material';
import {
  ExpandLess, ExpandMore, VerifiedUser, Assessment, PersonAdd,
  Edit, Category, Dashboard, Timeline
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import theme from './theme';
import HistorialDepartamento from './HistoricoRepresentantes';
const SUB_MODULE_CONFIG = {
  17: {
    path: '/verificar',
    icon: <VerifiedUser />,
    description: 'Verificar',
    requiredAction: 'LISTAR'
  },
  18: {
    path: '/reporteria',
    icon: <Assessment />,
    description: 'Reportería',
    requiredAction: 'LISTAR'
  },
  '18-historico': {
    path: '/historico-representantes',
    icon: <Timeline />,
    description: 'Histórico Gremial',
    requiredAction: 'LISTAR',
    parentModule: 18
  },
  20: {
    path: '/registro',
    icon: <PersonAdd />,
    description: 'Registro',
    requiredAction: 'AGREGAR'
  },
  22: {
    path: '/EditarRepresentantes',
    icon: <Edit />,
    description: 'Editar',
    requiredAction: 'MODIFICAR'
  },
  23: {
    path: '/categoria',
    icon: <Category />,
    description: 'Categorías',
    requiredAction: 'LISTAR'
  },

};

const NavigationMenu = ({ onItemClick }) => {
  const { userModules, getAvailableSubModules, hasActionPermission } = useAuth();
  const [expandedModules, setExpandedModules] = React.useState({});
  const [availableSubModules, setAvailableSubModules] = React.useState([]);
  const navigate = useNavigate();
  
  // Detectar si es móvil o desktop
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDrawer = !!onItemClick; // Si tiene onItemClick, es un drawer

  const handleModuleToggle = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const handleSubModuleClick = (subModule) => {
    const configKey = typeof subModule === 'string' ? subModule : subModule.IdSubModulo;
    const config = SUB_MODULE_CONFIG[configKey];

    if (config) {
      const moduleToCheck = config.parentModule || configKey;
      if (hasActionPermission(moduleToCheck, config.requiredAction)) {
        navigate(config.path);
      } else {
        navigate('/unauthorized');
      }
      if (onItemClick) onItemClick();
    }
  };

  const getVisibleSubModules = (subModules) => {
    return Object.keys(SUB_MODULE_CONFIG).map(id => {
      const subModuleId = parseInt(id);
      const config = SUB_MODULE_CONFIG[subModuleId];
      
      const userSubModule = subModules?.find(sub => sub.IdSubModulo === subModuleId);
      
      return {
        IdSubModulo: subModuleId,
        Descripcion: config.description,
        ACCION: userSubModule?.ACCION || [],
        hasAccess: hasActionPermission(subModuleId, config.requiredAction)
      };
    });
  };

  React.useEffect(() => {
    const loadSubModules = () => {
      try {
        const subModules = getAvailableSubModules();
        setAvailableSubModules(subModules);
      } catch (error) {
        console.error('Error loading submodules:', error);
        setAvailableSubModules([]);
      }
    };
    
    loadSubModules();
  }, [userModules]);

  // NAVEGACIÓN HORIZONTAL PARA DESKTOP
  if (!isDrawer && !isMobile) {
    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {Object.entries(SUB_MODULE_CONFIG).map(([id, config]) => {
          const subModuleId = config.parentModule || parseInt(id);
          const hasAccess = hasActionPermission(subModuleId, config.requiredAction);

          return (
            <Button
              key={id}
              onClick={() => {
                if (hasAccess) {
                  navigate(config.path);
                } else {
                  navigate('/unauthorized');
                }
              }}
              startIcon={config.icon}
              sx={{
                color: hasAccess ? '#432918' : '#999',
                opacity: hasAccess ? 1 : 0.6,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'rgba(67, 41, 24, 0.08)'
                }
              }}
            >
              {config.description}
            </Button>
          );
        })}
      </Box>
    );
  }

  // NAVEGACIÓN VERTICAL PARA DRAWER/MÓVIL (tu código original)
  return (
    <Box>
      <ListItemButton
        onClick={() => {
          navigate('/');
          if (onItemClick) onItemClick();
        }}
        sx={{
          borderRadius: 2,
          mb: 1,
          mx: 1,
          '&:hover': { bgcolor: 'rgba(67, 41, 24, 0.08)' }
        }}
      >
        <ListItemIcon>
          <Dashboard sx={{ color: '#432918' }} />
        </ListItemIcon>
        <ListItemText 
          primary="Inicio"
          secondary="Página principal"
        />
      </ListItemButton>
      
      <List>
        {userModules.map((module) => {
          const visibleSubModules = getVisibleSubModules(module.SubModulos);
          
          if (visibleSubModules.length === 0) return null;

          return (
            <Box key={module.IdModulo}>
              <ListItemButton
                onClick={() => handleModuleToggle(module.IdModulo)}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  mb: 0.5,
                  bgcolor: expandedModules[module.IdModulo] ? 'rgba(67, 41, 24, 0.04)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(67, 41, 24, 0.08)' }
                }}
              >
                <ListItemIcon>
                  <Category sx={{ color: '#432918' }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {module.Descripcion}
                      </Typography>
                      <Chip
                        size="small"
                        label={visibleSubModules.length}
                        sx={{ 
                          bgcolor: '#432918', 
                          color: 'white',
                          height: 20,
                          fontSize: '0.7rem'
                        }}
                      />
                    </Box>
                  }
                  secondary="Módulo del sistema"
                />
                {visibleSubModules.length > 0 && (
                  expandedModules[module.IdModulo] ? <ExpandLess /> : <ExpandMore />
                )}
              </ListItemButton>

              <Collapse 
                in={expandedModules[module.IdModulo]} 
                timeout="auto" 
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {visibleSubModules.map((subModule) => {
                    const config = SUB_MODULE_CONFIG[subModule.IdSubModulo];
                    
                    const availableActions = ['LISTAR', 'AGREGAR', 'MODIFICAR', 'ELIMINAR']
                      .filter(action => hasActionPermission(subModule.IdSubModulo, action));

                    return (
                      <ListItemButton
                        key={subModule.IdSubModulo}
                        onClick={() => handleSubModuleClick(subModule)}
                        sx={{
                          pl: 4,
                          borderRadius: 2,
                          mx: 2,
                          mb: 0.5,
                          opacity: subModule.hasAccess ? 1 : 0.6,
                          '&:hover': { bgcolor: 'rgba(67, 41, 24, 0.08)' }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {config.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {subModule.Descripcion}
                              </Typography>
                              {availableActions.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  {availableActions.map(action => (
                                    <Chip
                                      key={action}
                                      size="small"
                                      label={action.charAt(0)}
                                      title={action}
                                    />
                                  ))}
                                </Box>
                              )}
                              {!subModule.hasAccess && (
                                <Chip
                                  size="small"
                                  label="Sin acceso"
                                  color="error"
                                  sx={{ fontSize: '0.6rem' }}
                                />
                              )}
                            </Box>
                          }
                          secondary={config.description}
                          primaryTypographyProps={{
                            fontSize: '0.9rem'
                          }}
                          secondaryTypographyProps={{
                            fontSize: '0.8rem'
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>
      
      <Box sx={{ p: 2, mt: 2, bgcolor: '#f8f9fa', borderRadius: 2, mx: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Submódulos disponibles: {availableSubModules.filter(sub => {
            const config = SUB_MODULE_CONFIG[sub.IdSubModulo];
            return config && hasActionPermission(sub.IdSubModulo, config.requiredAction);
          }).length}
        </Typography>
      </Box>
    </Box>
  );
};

export default NavigationMenu;